/**
 * `/report/[sessionId]` — the canonical per-session report (D0, CORE-3/4).
 *
 * Dashboard, Library, Progress and the chat report card all open this screen.
 * It renders the persisted `analysis_structured` payload through the shared
 * report tree (`components/report/ReportView`), gates pending / processing /
 * failed / legacy states, and offers the PDF downloads.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { reviewDesk } from '@/design-system';
import { WebAppShell } from '@/design-system/components';
import { useAuth } from '@/context/AuthContext';
import { useTrialContext } from '@/context/TrialContext';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/navigation/routes';
import { chatAPI } from '@/services/api';
import { reportAPI } from '@/services/api/reportAPI';
import { trackAuthEvent } from '@/services/analytics';
import { analyticsErrorCode } from '@/services/analyticsPrivacy';
import { downloadBlob } from '@/utils/downloadBlob';
import { showDialog } from '@/utils/dialogs';
import { getAnalysisTierAccess } from '@/utils/planContext';
import { ReportBody, ReportHeader, reportStyles } from '@/components/report/ReportView';
import { ReportStateGate } from '@/components/report/ReportStateGate';
import {
  REPORT_POLL_INTERVAL_MS,
  reportGateState,
  reportViewedState,
  reportHeadline,
  shouldPollReport,
} from '@/components/report/reportModel';
import type { SessionReportResponse } from '@/types/report';
import { goBackOr } from '@/navigation/inAppHistory';

type LoadError = 'not_found' | 'locked' | 'error';

function errorKind(error: unknown): LoadError {
  const status = (error as { status?: number } | null)?.status;
  if (status === 404) return 'not_found';
  if (status === 402 || status === 403) return 'locked';
  return 'error';
}

export default function ReportScreen() {
  const params = useLocalSearchParams<{ sessionId?: string | string[] }>();
  const rawId = Array.isArray(params.sessionId) ? params.sessionId[0] : params.sessionId;
  const sessionId = rawId && /^\d+$/.test(rawId) ? Number(rawId) : null;

  const router = useRouter();
  const { t, language } = useTranslation();
  const { isPhone } = useResponsive();
  const { billingStatus } = useAuth();
  const { trialStatus, isTrialSession } = useTrialContext();
  const tierAccess = getAnalysisTierAccess(billingStatus?.plan_tier);

  const [report, setReport] = useState<SessionReportResponse | null>(null);
  const [loadError, setLoadError] = useState<LoadError | null>(sessionId ? null : 'not_found');
  const [loading, setLoading] = useState(Boolean(sessionId));
  const [pollAttempts, setPollAttempts] = useState(0);
  const [pdfPending, setPdfPending] = useState<'standard' | 'observation' | null>(null);
  const trackedRef = useRef(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await reportAPI.get(sessionId);
      setReport(response);
      setLoadError(null);
    } catch (error) {
      setLoadError(errorKind(error));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    setPollAttempts(0);
    setLoading(Boolean(sessionId));
    void load();
  }, [load, sessionId]);

  const gate = report
    ? reportGateState(report.status, Boolean(report.analysis_structured), pollAttempts)
    : null;

  // Poll while the analysis is still running (the row is written on completion).
  useEffect(() => {
    if (!gate || !shouldPollReport(gate)) return undefined;
    const timer = setTimeout(() => {
      setPollAttempts((value) => value + 1);
      void load();
    }, REPORT_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [gate, load, pollAttempts]);

  // Once per session and settled state: what the user saw (time-to-value end).
  const viewedRef = useRef<string | null>(null);
  useEffect(() => {
    const viewed = reportViewedState(gate);
    if (!sessionId || !viewed) return;
    const key = `${sessionId}:${viewed}`;
    if (viewedRef.current === key) return;
    viewedRef.current = key;
    trackAuthEvent('report_viewed', { session_id: sessionId, state: viewed });
  }, [gate, sessionId]);

  useEffect(() => {
    if (!sessionId || gate !== 'report' || trackedRef.current) return;
    trackedRef.current = true;
    if (isTrialSession(sessionId)) {
      trackAuthEvent('first_analysis_opened', {
        session_id: sessionId,
        trial_active: trialStatus?.active === true,
      });
    }
  }, [gate, isTrialSession, sessionId, trialStatus?.active]);

  const checkAgain = useCallback(() => {
    setPollAttempts(0);
    setLoading(true);
    void load();
  }, [load]);

  // Back to the screen the report was opened from (dashboard, Progress, chat,
  // sessions); opened from an email or a link, back to the sessions list.
  const goBack = useCallback(() => goBackOr(router, ROUTES.TABS_LIBRARY), [router]);

  const openSessions = useCallback(() => router.push(ROUTES.TABS_LIBRARY as never), [router]);
  const openChat = useCallback(() => {
    if (!sessionId) return;
    router.push({ pathname: ROUTES.TABS_CHAT, params: { sessionId: String(sessionId) } } as never);
  }, [router, sessionId]);

  const downloadPdf = useCallback(async (kind: 'standard' | 'observation') => {
    if (!sessionId || pdfPending) return;
    setPdfPending(kind);
    try {
      const blob = await chatAPI.exportSessionPdf(sessionId, language, kind);
      const suffix = kind === 'observation' ? '-observation' : '';
      await downloadBlob(blob, `mcai-report-${sessionId}${suffix}.pdf`, t('analysis.exportPdf'));
      trackAuthEvent('report_pdf_downloaded', { session_id: sessionId, pdf_kind: kind });
    } catch (error: any) {
      trackAuthEvent('report_pdf_failed', { session_id: sessionId, pdf_kind: kind, error_code: analyticsErrorCode(error) });
      const detail = error?.response?.status === 409 ? error?.response?.data?.detail : null;
      showDialog(detail || t('chat.exportPdfFailed'));
    } finally {
      setPdfPending(null);
    }
  }, [language, pdfPending, sessionId, t]);

  const structured = report?.analysis_structured || null;
  const dateText = report?.last_analyzed_at || report?.created_at
    ? new Date((report?.last_analyzed_at || report?.created_at) as string).toLocaleDateString(language)
    : null;

  const pdfActions = gate === 'report' ? (
    <View style={styles.pdfActions}>
      <Pressable
        testID="report_pdf_standard"
        accessibilityRole="button"
        onPress={() => downloadPdf('standard')}
        disabled={pdfPending !== null}
        style={reportStyles.secondaryButton}
      >
        {pdfPending === 'standard'
          ? <ActivityIndicator size="small" color={reviewDesk.accent} />
          : <MaterialIcons name="download" size={18} color={reviewDesk.accent} />}
        <Text style={reportStyles.secondaryButtonText}>{t('chat.downloadStandardPdf')}</Text>
      </Pressable>
      {tierAccess.allowCustomMode && (
        <Pressable
          testID="report_pdf_observation"
          accessibilityRole="button"
          onPress={() => downloadPdf('observation')}
          disabled={pdfPending !== null}
          style={reportStyles.secondaryButton}
        >
          {pdfPending === 'observation'
            ? <ActivityIndicator size="small" color={reviewDesk.accent} />
            : <MaterialIcons name="assignment" size={18} color={reviewDesk.accent} />}
          <Text style={reportStyles.secondaryButtonText}>{t('chat.downloadObservationPdf')}</Text>
        </Pressable>
      )}
    </View>
  ) : null;

  let content: React.ReactNode;
  if (loading && !report) {
    content = (
      <View testID="report_loading" style={styles.center}>
        <ActivityIndicator color={reviewDesk.accent} />
      </View>
    );
  } else if (!report || (loadError && !report)) {
    const locked = loadError === 'locked';
    content = (
      <View testID={`report_error_${loadError || 'error'}`} style={[reportStyles.card, styles.stateCard]}>
        <Text style={[reportStyles.sectionTitle, styles.centerText]}>
          {locked
            ? t('report.state.lockedTitle')
            : loadError === 'not_found'
              ? t('report.state.notFound')
              : t('report.state.loadError')}
        </Text>
        {locked && <Text style={[reportStyles.body, styles.centerText]}>{t('report.state.lockedBody')}</Text>}
        <View style={styles.pdfActions}>
          {locked ? (
            <Pressable accessibilityRole="button" onPress={() => router.push(ROUTES.GATED_PLAN_SELECTION as never)} style={reportStyles.primaryButton}>
              <Text style={reportStyles.primaryButtonText}>{t('report.state.seePlans')}</Text>
            </Pressable>
          ) : loadError === 'error' ? (
            <Pressable accessibilityRole="button" onPress={checkAgain} style={reportStyles.primaryButton}>
              <Text style={reportStyles.primaryButtonText}>{t('report.state.checkAgain')}</Text>
            </Pressable>
          ) : null}
          <Pressable accessibilityRole="button" onPress={openSessions} style={reportStyles.secondaryButton}>
            <Text style={reportStyles.secondaryButtonText}>{t('report.state.goToSessions')}</Text>
          </Pressable>
        </View>
      </View>
    );
  } else {
    const title = report.title || t('report.sessionFallback', { id: report.session_id });
    const headline = reportHeadline(structured);
    content = (
      <>
        <ReportHeader
          title={title}
          meta={dateText ? [t('report.analyzedOn', { date: dateText })] : []}
          levelKey={headline.levelKey}
          levelText={headline.menteeLabel}
          confidenceLabel={(structured?.level_decision?.confidence_label as string) || null}
          actions={pdfActions}
        />
        {report.status === 'excluded' && (
          <View style={styles.notice}>
            <MaterialIcons name="info-outline" size={18} color={reviewDesk.accent} />
            <Text style={[reportStyles.body, styles.flexOne]}>{t('report.excludedNote')}</Text>
          </View>
        )}
        {gate === 'report' && structured ? (
          <ReportBody structured={structured} />
        ) : gate && gate !== 'report' ? (
          <ReportStateGate
            state={gate}
            onCheckAgain={checkAgain}
            onOpenSessions={openSessions}
            onOpenChat={openChat}
          />
        ) : null}
      </>
    );
  }

  return (
    <WebAppShell active="sessions" contentMaxWidth="100%" scroll={false}>
      <ScrollView
        testID="report_screen"
        nativeID="report_screen"
        {...({ dataSet: { phNoCapture: 'true' } } as object)}
        style={styles.screen}
        contentContainerStyle={[styles.content, isPhone && styles.contentPhone]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('report.back')}
          onPress={goBack}
          style={({ pressed }) => [styles.back, pressed && styles.pressed]}
        >
          <MaterialIcons name="chevron-left" size={22} color={reviewDesk.accent} />
          <Text style={styles.backText}>{t('report.back')}</Text>
        </Pressable>
        {content}
      </ScrollView>
    </WebAppShell>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: reviewDesk.canvas },
  content: { width: '100%', maxWidth: 1240, alignSelf: 'center', padding: 28, gap: 16 },
  contentPhone: { padding: 16, gap: 14 },
  center: { minHeight: 240, alignItems: 'center', justifyContent: 'center' },
  stateCard: { alignItems: 'center', paddingVertical: 32 },
  centerText: { textAlign: 'center' },
  pdfActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, flexShrink: 1, minWidth: 0, maxWidth: '100%' },
  notice: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    borderRadius: 12,
    backgroundColor: reviewDesk.accentSoft,
  },
  flexOne: { flex: 1, minWidth: 0 },
  back: { minHeight: 40, alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 2 },
  backText: { color: reviewDesk.accent, fontSize: 15, fontWeight: '700' },
  pressed: { opacity: 0.7 },
});
